/* eslint-disable */
// Session 60.11 · /contact — full contact page for members who prefer a
// hosted message form over a mailto: link. Delivers to contact@workmetier.com
// via POST /api/contact, persists to db.contact_submissions, and pings the
// admin bell. Public — no auth required. Preserves the existing mailto
// fallback via the ContactBlock so nothing regresses.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

export default function Contact() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const canSubmit =
    !busy &&
    name.trim().length > 0 &&
    /^\S+@\S+\.\S+$/.test(email.trim()) &&
    message.trim().length >= 3;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr("");
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim() || null,
        message: message.trim(),
      });
      setSent(true);
    } catch (ex) {
      setErr(
        ex?.response?.data?.detail ||
          "Something went wrong sending your message. Try again or email contact@workmetier.com directly.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="m-contact-page" style={{ minHeight: "100vh", background: "#FAF8F3", color: "#1B2340" }}>
      <header
        style={{
          padding: "22px 36px",
          borderBottom: "1px solid rgba(27,35,64,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 12, textDecoration: "none", color: "inherit" }}>
          <img src="/brand/metier-icon.png" alt="Métier" style={{ width: 34, height: 34 }} />
          <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, letterSpacing: "0.05em" }}>MÉTIER</span>
        </Link>
        <nav style={{ display: "flex", gap: 18, fontSize: 13 }}>
          <Link to="/spaces" style={{ color: "inherit", textDecoration: "none" }}>Workspaces</Link>
          <Link to="/hours" style={{ color: "inherit", textDecoration: "none" }}>Métier Hours</Link>
          <Link to="/login" style={{ color: "var(--gold, #B8913A)", textDecoration: "none", fontWeight: 600 }}>Sign in →</Link>
        </nav>
      </header>

      <main
        style={{
          maxWidth: 1080,
          margin: "0 auto",
          padding: "56px 24px 80px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 48,
          alignItems: "start",
        }}
        className="m-contact-grid"
      >
        {/* LEFT — copy + social */}
        <section>
          <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#B8913A", fontWeight: 600 }}>
            Contact us
          </div>
          <h1
            style={{
              fontFamily: "'Playfair Display', 'Cormorant Garamond', Georgia, serif",
              fontWeight: 500,
              fontSize: 44,
              lineHeight: 1.1,
              margin: "10px 0 14px",
            }}
            data-testid="contact-page-heading"
          >
            Say <em>hello</em>.
          </h1>
          <p style={{ fontSize: 16, lineHeight: 1.65, color: "rgba(27,35,64,0.75)", margin: 0, maxWidth: 460 }}>
            Real humans read every message. Reach the team by{" "}
            <a
              href="mailto:contact@workmetier.com"
              data-testid="contact-page-copy-email"
              style={{ color: "#B8913A", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid rgba(184,145,58,0.4)" }}
            >
              email
            </a>
            , or follow along for what&#39;s new.
          </p>

          <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(27,35,64,0.55)", fontWeight: 600, marginBottom: 4 }}>
                Email
              </div>
              <a
                href="mailto:contact@workmetier.com"
                data-testid="contact-page-email"
                style={{ fontSize: 15, fontWeight: 600, color: "#B8913A", textDecoration: "none", borderBottom: "1px solid rgba(184,145,58,0.4)", paddingBottom: 2 }}
              >
                contact@workmetier.com &rarr;
              </a>
            </div>

            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(27,35,64,0.55)", fontWeight: 600, marginBottom: 6 }}>
                Follow
              </div>
              {/* Session 60.14 · Text link reads cleaner than a lone icon on
                  this page. Kept the SocialLinks component elsewhere
                  (login aside, app footer) where it sits alongside other
                  chrome and doesn't look orphaned. */}
              <a
                href="https://www.instagram.com/workmetier"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-page-instagram"
                style={{ fontSize: 15, fontWeight: 600, color: "#B8913A", textDecoration: "none", borderBottom: "1px solid rgba(184,145,58,0.4)", paddingBottom: 2 }}
              >
                Follow us on Instagram &rarr;
              </a>
            </div>
          </div>
        </section>

        {/* RIGHT — form */}
        <section
          style={{
            padding: 32,
            borderRadius: 16,
            background: "#FFFFFF",
            border: "1px solid rgba(27,35,64,0.10)",
            boxShadow: "0 8px 30px rgba(27,35,64,0.06)",
          }}
        >
          {sent ? (
            <div data-testid="contact-form-success">
              <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#B8913A", fontWeight: 600 }}>
                Message sent
              </div>
              <h2
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 500,
                  fontSize: 28,
                  margin: "6px 0 12px",
                }}
              >
                Thanks {name.split(" ")[0] || "there"} — we&#39;ll be in touch.
              </h2>
              <p style={{ fontSize: 14.5, lineHeight: 1.65, color: "rgba(27,35,64,0.75)", margin: 0 }}>
                A real person will reply to <strong>{email}</strong> within a day or two.
                In the meantime,{" "}
                <a
                  href="https://www.instagram.com/workmetier"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-success-instagram"
                  style={{ color: "#B8913A", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid rgba(184,145,58,0.4)" }}
                >
                  follow us on Instagram
                </a>{" "}
                for what&#39;s new.
              </p>
              <button
                type="button"
                onClick={() => { setSent(false); setSubject(""); setMessage(""); }}
                data-testid="contact-form-reset"
                style={{
                  marginTop: 24,
                  background: "none",
                  border: "1px solid rgba(27,35,64,0.20)",
                  padding: "10px 16px",
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  color: "inherit",
                }}
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="m-contact-form" data-testid="contact-form">
              <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#B8913A", fontWeight: 600 }}>
                Send us a message
              </div>
              <h2
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 500,
                  fontSize: 26,
                  margin: "4px 0 20px",
                }}
              >
                We&#39;ll get back to you.
              </h2>

              <Field label="Your name">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  required
                  data-testid="contact-name"
                  style={inputStyle}
                />
              </Field>

              <Field label="Email">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="contact-email"
                  style={inputStyle}
                />
              </Field>

              <Field label="Subject (optional)">
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={200}
                  placeholder="What is this about?"
                  data-testid="contact-subject"
                  style={inputStyle}
                />
              </Field>

              <Field label={`Message · ${message.length}/5000`}>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={5000}
                  rows={7}
                  required
                  placeholder="Tell us what's on your mind."
                  data-testid="contact-message"
                  style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
                />
              </Field>

              {err && (
                <div
                  data-testid="contact-form-error"
                  style={{ marginTop: 8, padding: "10px 12px", background: "rgba(179,65,65,0.08)", border: "1px solid rgba(179,65,65,0.28)", borderRadius: 8, fontSize: 13, color: "#b34141" }}
                >
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="contact-submit"
                style={{
                  marginTop: 18,
                  width: "100%",
                  padding: "13px 16px",
                  borderRadius: 999,
                  border: "1px solid rgba(184,145,58,0.6)",
                  background: canSubmit ? "linear-gradient(135deg,#D6B559 0%,#B8913A 55%,#8B6A22 100%)" : "rgba(184,145,58,0.35)",
                  color: canSubmit ? "#FFF8E4" : "rgba(255,248,228,0.85)",
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  transition: "transform 120ms ease, opacity 120ms ease",
                }}
              >
                {busy ? "Sending…" : "Send message"}
              </button>

              <p style={{ marginTop: 12, fontSize: 12, color: "rgba(27,35,64,0.55)", textAlign: "center" }}>
                Prefer email? <a href="mailto:contact@workmetier.com" style={{ color: "#B8913A", textDecoration: "none" }}>Reach us directly →</a>
              </p>
            </form>
          )}
        </section>
      </main>

      <footer
        style={{
          padding: "20px 36px",
          borderTop: "1px solid rgba(27,35,64,0.08)",
          fontSize: 12,
          color: "rgba(27,35,64,0.55)",
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <span>© Métier · workmetier.com</span>
        <div style={{ display: "inline-flex", gap: 14, alignItems: "center" }}>
          <Link to="/terms" style={{ color: "inherit", textDecoration: "none" }}>Terms</Link>
          <Link to="/privacy" style={{ color: "inherit", textDecoration: "none" }}>Privacy</Link>
          <a
            href="https://www.instagram.com/workmetier"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-footer-instagram"
            style={{ color: "inherit", textDecoration: "none" }}
          >
            Instagram
          </a>
        </div>
      </footer>

      <style>{`
        @media (max-width: 900px) {
          .m-contact-grid {
            grid-template-columns: 1fr !important;
            padding: 32px 20px 60px !important;
            gap: 32px !important;
          }
        }
      `}</style>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid rgba(27,35,64,0.15)",
  borderRadius: 10,
  fontSize: 14,
  background: "#FDFCF8",
  color: "#1B2340",
  outline: "none",
  transition: "border-color 120ms ease, box-shadow 120ms ease",
};

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(27,35,64,0.65)", fontWeight: 600, marginBottom: 6 }}>
        {label}
      </div>
      {children}
    </label>
  );
}
